import { BalloonEventModel, MetadataModel } from '../../models';
import BalloonEventsRepository from '../BalloonEventsRepository';
import BeatAPIBalloonEventTransformer from './BeatAPIBalloonEventTransformer';
import BeatAPIInverseBalloonEventTransformer from './BeatAPIInverseBalloonEventTransformer';
import { EventCollectionModel } from '../../models/EventCollectionModel';
import {
  BalloonEventsQuery,
  ApiEndpoints as FullApiEndpoints,
} from '../../types';
import Transformer from '../../core/Transformer';
import moment from 'moment';
import { ImageMetadata, ImageUploadData } from '../../types/ImageSource';
import { apiFetch } from '../../utils/apiFetch';

interface ApiEndpoints extends Partial<FullApiEndpoints> {
  search: string;
  balloons: string;
  files: string;
  balloonCollections: string;
}

export default class BeatAPIBalloonEventsRepository extends BalloonEventsRepository {
  protected _abortController: AbortController | null = null;
  protected _quadrantAbortControllers: (AbortController | null)[] = [
    null,
    null,
    null,
    null,
    null,
  ];
  protected _apiKey: string;
  protected _partnerKey: string | null = null;
  protected _apiEndpoints: ApiEndpoints;
  protected _logs = false;
  protected _transformerPlugins: Transformer<
    BalloonEventModel,
    BalloonEventModel
  >[] = [];

  protected _transfomer: BeatAPIBalloonEventTransformer;

  set metadata(metadata: MetadataModel) {
    super.metadata = metadata;
    this._transfomer = new BeatAPIBalloonEventTransformer(
      this._builder,
      metadata,
      this._logs,
      this._transformerPlugins
    );
  }

  set apiKey(apiKey: string) {
    this._apiKey = apiKey;
  }

  set partnerKey(partnerKey: string | null) {
    this._partnerKey = partnerKey;
  }

  set apiEndpoints(apiEndpoints: ApiEndpoints) {
    this._apiEndpoints = apiEndpoints;
  }

  private _inverseTransformer = new BeatAPIInverseBalloonEventTransformer();

  protected _excludedProviders: string[] = [];

  constructor(
    metadata: MetadataModel,
    apiEndpoints: ApiEndpoints,
    apiKey: string,
    logs = false,
    transformerPlugins: Transformer<
      BalloonEventModel,
      BalloonEventModel
    >[] = [],
    excludedProviders: string[] = [],
    partnerApiKey: string | null = null
  ) {
    super(metadata);
    this._apiKey = apiKey;
    this._apiEndpoints = apiEndpoints;
    this._logs = logs;
    this._transformerPlugins = transformerPlugins;
    this._transfomer = new BeatAPIBalloonEventTransformer(
      this._builder,
      metadata,
      this._logs,
      transformerPlugins
    );
    this._excludedProviders = excludedProviders;
    this._partnerKey = partnerApiKey;
  }

  protected _getQueryObject(quadrant?: number) {
    const queryObject: any = {};
    if (this.query.categories && this.query.categories.length > 0) {
      queryObject.categories = this.query.categories;
    }
    if (this.query.bbox) {
      const bbox =
        quadrant !== undefined
          ? this.query.quadrants[quadrant]!
          : this.query.bbox!;
      const nw = bbox.getNorthWest();
      const se = bbox.getSouthEast();
      queryObject.topLeft = [nw?.lat.toFixed(4), nw?.lng.toFixed(4)];
      queryObject.bottomRight = [se?.lat.toFixed(4), se?.lng.toFixed(4)];
    }
    if ((this.query.dates?.length ?? 0) > 0) {
      // Explicit date filter: use start of that day
      const fromDate = new Date(this.query.fromDate);
      fromDate.setUTCHours(0, 0, 0, 0);
      queryObject.dateFrom = fromDate.toISOString();
    } else {
      // Default (no date filter): use now - 6h to include recently-started ongoing events
      queryObject.dateFrom = new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString();
    }
    if (this.query.untilDate) {
      const untilDate = new Date(this.query.untilDate);
      untilDate.setUTCHours(0, 0, 0, 0);
      queryObject.dateTo = untilDate.toISOString();
    }
    if (this.query.text) {
      queryObject.textSearch = this.query.text;
    }
    queryObject.amount = this.query.amount;
    if (this.query.sources.length > 0) {
      queryObject.providers = this.query.sources.filter(
        (s) => !this._excludedProviders.includes(s)
      );
      if (queryObject.providers.length === 0) {
        queryObject.providers = this._metadata.providers
          .map((p) => p.shortName)
          .filter((p) => !this._excludedProviders.includes(p));
      }
    } else {
      queryObject.providers = this._metadata.providers
        .map((p) => p.shortName)
        .filter((p) => !this._excludedProviders.includes(p));
    }
    if (this.query.page) {
      queryObject.page = this.query.page;
    }
    if (this.query.regionArea.length > 0) {
      queryObject.regionArea = this.query.regionArea;
    }
    if (this.query.region.length > 0) {
      queryObject.region = this.query.region;
    }
    if (this.query.tags.length > 0) {
      queryObject.tags = this.query.tags;
    }
    if (this.query.user.length > 0) {
      queryObject.user = this.query.user;
    }
    // Partner-profile scope: override the providers list with the partner's own
    // data-source-provider short name(s). Runs AFTER the sources/all-providers
    // block above so it is not clobbered, and is passed raw (no excluded-provider
    // filtering) so a partner's own events always show on its profile.
    if (this.query.providers.length > 0) {
      queryObject.providers = this.query.providers;
    }
    if (this.query.mustHaveAtLeastOneImage) {
      queryObject.mustHaveAtLeastOneImage = this.query.mustHaveAtLeastOneImage;
    }
    return queryObject;
  }

  protected async _fetchBalloonEvents(
    ids: string[]
  ): Promise<BalloonEventModel[]> {
    // add body as query
    const params = new URLSearchParams();
    params.append('ids', ids.join(','));
    const response = await apiFetch(
      this._apiEndpoints.search + '?' + params.toString(),
      {
        method: 'GET',
        cache: 'no-cache',
        apiKey: this._apiKey,
        partnerKey: this._partnerKey,
      }
    );
    const data = await response.json();
    const events = data.data
      .map((hit: any) => {
        try {
          return this._transfomer.transform(hit);
        } catch (e) {
          if (this._logs) console.error(e, hit);
          return null;
        }
      })
      .filter((e: BalloonEventModel | null) => e) as BalloonEventModel[];
    return events;
  }

  public async post(
    event: BalloonEventModel | EventCollectionModel,
    token: string
  ): Promise<{ status: number; data?: any }> {
    if ('_events' in event) {
      const collectionResponse = await apiFetch(
        `${this._apiEndpoints.balloonCollections}/new`,
        {
          method: 'POST',
          body: JSON.stringify({
            name: event.name,
            type: 'multi-location',
          }),
          apiKey: this._apiKey,
          partnerKey: this._partnerKey,
          authToken: token,
        }
      );
      const collectionData = await collectionResponse.json();
      const collection: {
        id: string;
        name: string;
        slug: string;
        type: string;
      } = collectionData.data;
      console.warn('EventCollectionModel not implemented fully');
      const promises = event.events.map((e) => {
        e.collectionId = collection.id;
        return this.post(e, token);
      });
      const response = await Promise.all(promises);
      console.log('EventCollection posted as singular events');
      return response[0];
    }

    const response = await apiFetch(`${this._apiEndpoints.balloons}`, {
      method: 'POST',
      body: JSON.stringify(this._inverseTransformer.transform(event)),
      apiKey: this._apiKey,
      partnerKey: this._partnerKey,
      authToken: token,
    });
    const data = await response.json();
    console.log(data);
    return { status: response.status, data };
  }

  public async put(
    balloonId: string,
    event: BalloonEventModel,
    token: string
  ): Promise<{ status: number; data?: any }> {
    const response = await apiFetch(`${this._apiEndpoints.balloons}/${balloonId}`, {
      method: 'PUT',
      body: JSON.stringify(this._inverseTransformer.transform(event)),
      apiKey: this._apiKey,
      partnerKey: this._partnerKey,
      authToken: token,
    });
    const data = await response.json();
    console.log('[put] Update response:', data);

    // If successful, reload the balloon to get fresh data and update cache
    if (response.status === 200 || response.status === 201) {
      try {
        await this.load(balloonId, true);
      } catch (e) {
        console.warn('[put] Failed to reload balloon after update:', e);
      }
    }

    return { status: response.status, data };
  }

  protected async _fetchBalloonEventsQuery(
    cancel: boolean,
    quadrant?: number
  ): Promise<BalloonEventModel[]> {
    let abortController = quadrant
      ? this._quadrantAbortControllers[quadrant]
      : this._abortController;
    if (abortController && cancel) {
      abortController.abort('params changed');
    }
    abortController = new AbortController();
    if (quadrant) {
      this._quadrantAbortControllers[quadrant] = abortController;
    } else {
      this._abortController = abortController;
    }
    const regionAreas = [
      ...(this.query.regionArea || []),
      ...(this.query.region || []),
    ];
    const queryObject = this._getQueryObject(quadrant);
    const { signal } = abortController;
    const params = new URLSearchParams();
    for (const key in queryObject) {
      let q = queryObject[key];
      if (Array.isArray(q)) {
        q = q.join(',');
      }
      params.append(key, q);
    }
    const response = await apiFetch(
      this._apiEndpoints.search + '?' + params.toString(),
      {
        signal,
        method: 'GET',
        cache: 'no-cache',
        apiKey: this._apiKey,
        partnerKey: this._partnerKey,
      }
    );
    let data;
    try {
      data = await response.json();
      const events = data.data
        .map((hit: any) => {
          try {
            const transformed = this._transfomer.transform(hit);
            if (regionAreas) {
              for (const area of regionAreas) {
                transformed.addRegionArea(area);
              }
            }
            return transformed;
          } catch (e) {
            if (this._logs) console.error(e, hit);
            return null;
          }
        })
        .filter((e: BalloonEventModel | null) => e) as BalloonEventModel[];
      if (this._logs) {
        console.log('\n --- BeatAPIBalloonEventsRepository --- \n');
        console.log(this._apiEndpoints.search, queryObject, data, events);
        if (data.elasticQuery && data.elasticQuery.query)
          console.log(JSON.stringify(data.elasticQuery.query));
      }
      return events;
    } catch (e) {
      console.error(response, e, queryObject);
      return [];
    }
  }

  protected async _fetchBalloonEvent(
    id: string,
    invalidateChache: boolean
  ): Promise<BalloonEventModel> {
    const response = await apiFetch(
      `${this._apiEndpoints.balloons}/${id}${
        invalidateChache ? '?cache-refresh=' + Date.now() : ''
      }`,
      {
        method: 'GET',
        cache: 'no-cache',
        apiKey: this._apiKey,
        partnerKey: this._partnerKey,
      }
    );
    const data = await response.json();

    const item = this._transfomer.transform(data.data);
    if (
      item.provider &&
      this._excludedProviders.includes(item.provider?.shortName)
    ) {
      throw new Error('Provider not allowed');
    }
    return item;
  }

  public async uploadFile(
    balloonId: string,
    authToken: string,
    uploadData: ImageUploadData
  ): Promise<{ status: number; data?: any }> {
    const formData = new FormData();
    formData.append('balloonId', balloonId);
    formData.append('files[]', uploadData.file, uploadData.file.name);

    if (uploadData.metadata?.copyright) {
      formData.append('copyrights[]', uploadData.metadata!.copyright!);
    }

    /*
    TODO: Implement in backend
    if (metadata?.text) {
      formData.append('text', metadata?.text);
    }*/

    const response = await apiFetch(`${this._apiEndpoints.files}/uploads`, {
      method: 'POST',
      body: formData,
      apiKey: this._apiKey,
      partnerKey: this._partnerKey,
      authToken: authToken,
    });

    const data = await response.json();
    return { status: response.status, data };
  }

  public async deleteBalloon(
    balloonEventId: string,
    authToken: string
  ): Promise<{ status: number; data?: any }> {
    const response = await apiFetch(
      `${this._apiEndpoints.balloons}/${balloonEventId}`,
      {
        method: 'DELETE',
        apiKey: this._apiKey,
        partnerKey: this._partnerKey,
        authToken: authToken,
      }
    );
    const data = await response.json();
    return { status: response.status, data };
  }

  protected async _rawQuery(
    query: BalloonEventsQuery,
    page: number = 1,
    abortController: AbortController
  ): Promise<BalloonEventModel[]> {
    const { signal } = abortController;

    const regionAreas = [
      ...(this.query.regionArea || []),
      ...(this.query.region || []),
    ];
    const restructuredQuery: any = {};
    if (query.categories) {
      restructuredQuery.categories = query.categories;
    }
    if (query.text) {
      restructuredQuery.textSearch = query.text;
    }
    if (query.bbox) {
      const ne =
        'northEast' in query.bbox
          ? query.bbox.northEast
          : query.bbox.getNorthEast();
      const sw =
        'southWest' in query.bbox
          ? query.bbox.southWest
          : query.bbox.getSouthWest();
      restructuredQuery.topLeft = [ne.lat, sw.lng];
      restructuredQuery.bottomRight = [sw.lat, ne.lng];
    }
    if (query.dates && query.dates.length > 0) {
      let dateFrom = query.dates[0];
      restructuredQuery.dateFrom = dateFrom.toISOString();
      if (query.dates.length > 1) {
        let dateTo = query.dates[1];
        dateTo = moment(dateTo).endOf('hour').toDate();
        restructuredQuery.dateTo = dateTo.toISOString();
      }
    }

    if (query.mustHaveAtLeastOneImage) {
      restructuredQuery.mustHaveAtLeastOneImage = query.mustHaveAtLeastOneImage;
    }

    if (query.tags) {
      restructuredQuery.tags = query.tags;
    }

    if (query.region) {
      restructuredQuery.region = query.region;
    }

    if (query.regionArea) {
      restructuredQuery.regionArea = query.regionArea;
    }

    if (query.sources) {
      restructuredQuery.providers = query.sources.filter(
        (s) => !this._excludedProviders.includes(s)
      );
      if (restructuredQuery.providers.length === 0) {
        restructuredQuery.providers = this._metadata.providers
          .map((p) => p.shortName)
          .filter((p) => !this._excludedProviders.includes(p));
      }
    } else {
      restructuredQuery.providers = this._metadata.providers
        .map((p) => p.shortName)
        .filter((p) => !this._excludedProviders.includes(p));
    }

    // Partner-profile scope: override with the partner's own provider short name(s),
    // raw (after the sources/all-providers fallback so it isn't clobbered).
    if (query.providers && query.providers.length > 0) {
      restructuredQuery.providers = query.providers;
    }

    restructuredQuery.amount = query.amount || 20;
    restructuredQuery.page = page;

    const params = new URLSearchParams();
    for (const key in restructuredQuery) {
      let q = restructuredQuery[key];
      if (Array.isArray(q)) {
        q = q.join(',');
      }
      params.append(key, q);
    }
    const response = await apiFetch(
      this._apiEndpoints.search + '?' + params.toString(),
      {
        signal,
        cache: 'no-cache',
        method: 'GET',
        apiKey: this._apiKey,
        partnerKey: this._partnerKey,
      }
    );
    const data = await response.json();
    if (this._logs) console.log('pre-transform:', data);
    const transformed = data.data
      .map((hit: any) => {
        try {
          const transformed = this._transfomer.transform(hit);
          for (const area of regionAreas) {
            transformed.addRegionArea(area);
          }
          return transformed;
        } catch (e) {
          if (this._logs) console.error(e, hit);
          return null;
        }
      })
      .filter((e: BalloonEventModel | null) => e) as BalloonEventModel[];
    if (this._logs) console.log('post-transform:', transformed);
    return transformed;
  }
}
